<template>
  <div id="employees-modal-parent">
    <Page
      title="従業員管理"
      :departmentPath="selectedDepartmentPath"
      helpInfoTitle="属性情報に変更が必要な場合は管理者へご依頼ください"
    >
      <template v-slot:action>
        <ActionButton
          v-if="availableRegistration"
          title="受検結果ﾀﾞｳﾝﾛｰﾄﾞ"
          color="primary"
          shape="round"
          width="180"
          @click="onResultsExportButtonClick"
          uk-tooltip="title: 部署の絞り込みと同期してダウンロードします; pos: left; cls: uk-active uk-width-auto;"
        />
      </template>
      <template v-slot:content>
        <div class="table-action">
          <form class="serach-form" @submit.prevent="onEmployeeSearch()">
            <div class="uk-margin-small-bottom">
              <div class="display-info">
                <span>合計：{{ totalCount }}件</span>
              </div>
              <ActionButton
                v-if="availableRegistration || isEditableEmployee"
                title="新規追加"
                color="default"
                icon="plus"
                @click="onAddButtonClick"
                uk-tooltip="title: 従業員を１件作成します; pos: top-left; cls: uk-active uk-width-auto;"
              />
              <div class="excel-button">
                <FileSelectorButton
                  v-if="availableRegistration"
                  title="Excel登録"
                  icon="plus"
                  accept=".xlsx"
                  @select-file="onSelectExcel"
                  uk-tooltip="title: 従業員を一括登録できます<br />テンプレートをダンロードしてご利用ください; pos: top-right; cls: uk-active uk-width-auto;"
                />
              </div>
            </div>
            <div v-if="availableRegistration" class="table-sub-action">
              <a href="/#" @click="onDownloadTemplateFile">テンプレートダウンロード</a>
            </div>
            <div class="serach-items">
              <div class="serach-header">名前:</div>
              <input
                class="uk-input serach-box name"
                type="text"
                v-model="FormValues.name"
                placeholder="部分一致"
                @keypress.enter="onEmployeeSearch()"
              />
              <div class="serach-header">職種:</div>
              <div class="serach-box">
                <Multiselect
                  class="multiselect-blue occupation"
                  v-model="FormValues.occupations"
                  v-bind="occupations"
                  placeholder="複数選択"
                  noOptionsText="読込中です"
                />
              </div>
              <div class="serach-header">役職:</div>
              <div class="serach-box">
                <Multiselect
                  class="multiselect-blue position"
                  v-model="FormValues.positions"
                  v-bind="positions"
                  placeholder="複数選択"
                  noOptionsText="読込中です"
                />
              </div>
              <div class="serach-header">最終受検日:</div>
              <Datepicker
                width="180"
                class="uk-input start-date"
                v-model="FormValues.startDate"
                :locale="locale"
                input-format="yyyy-MM-dd"
                :weekStartsOn="0"
                placeholder="From"
                :clearable="true"
              />
              <div>〜</div>
              <Datepicker
                class="uk-input end-date"
                v-model="FormValues.endDate"
                :locale="locale"
                input-format="yyyy-MM-dd"
                :weekStartsOn="0"
                placeholder="To"
                :clearable="true"
              />
              <div class="right-contents">
                <ActionButton title="検索" color="primary" type="submit" :width="110" />
                <ActionButton title="クリア" color="default" type="button" @click="resetConditions" :width="110" />
              </div>
            </div>
          </form>
        </div>
        <div class="uk-card uk-card-default uk-card-body">
          <Table ref="tableComponent" :header="header" :callback="getTableData" @column-click="onColumnClick" />
        </div>
      </template>
    </Page>
    <div class="uk-flex-top employee-form-modal" uk-modal ref="modal" container="#employees-modal-parent" :bg-close="false">
      <EmployeeForm :key="formKey" :employee="employee" @submitted="onSubmitted" />
    </div>
  </div>
</template>
<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss" scoped>
@include table-action;
@include multiselect;
.table-action > form > div {
  display: flex;
  justify-content: flex-end;
}
.table-sub-action {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $spacing-small;
  font-size: 0.7rem;
}
.search-field {
  margin-right: $spacing-medium;
}
.excel-button {
  margin-left: $spacing-small;
}
.display-info,
.display-limit,
.table-action > form > div.serach-items {
  display: flex;
  flex-direction: row;
  align-items: center;
  vertical-align: center;
  text-align: left;
  margin-right: auto;
  justify-content: flex-start;
}
.display-limit {
  margin-left: 30px;
  & > label {
    margin-right: 16px;
  }
}
.serach-items {
  min-width: 1100px;
  font-size: 12px;
  .serach-header {
    word-break: keep-all;
  }
  .serach-box {
    box-sizing: border-box;
    margin-right: $spacing-ex-small;
  }
  .name {
    width: 8em;
  }
  .occupation {
    width: 12em;
  }
  .position {
    width: 12em;
  }
  div.v3dp__datepicker {
    width: 8em;
    &:deep(div.v3dp__clearable) {
      position: absolute;
      right: 9px;
      top: 10px;
      left: unset;
      cursor: pointer;
    }
  }
  & > div {
    margin-right: 6px;
  }
  .right-contents {
    margin: 0 0 0 auto;
    & > button:first-child {
      margin-right: 6px;
    }
  }
}
.total-count {
  margin-right: 16px;
}
</style>
<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Ref, Watch } from "vue-property-decorator";
import {
  DepartmentApi,
  Employee,
  SystemApi,
  ClientApi,
  EmployeeApi,
  UploadItemEnum,
  GetEmployeeProfile,
  RoleName,
  AuthApi,
  Occupation,
  Position,
  AsExportTypeEnum
} from "../../autogen";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import Table from "../molecules/Table.vue";
import UIkit from "uikit";
import SearchField from "../atoms/SearchField.vue";
import FileSelectorButton from "../molecules/FileSelectorButton.vue";
import { RouteLocation } from "vue-router";
import EmployeeForm from "../organisms/EmployeeForm.vue";
import Store from "../../store";
import { InstructionLevel } from "../../types";
import Multiselect from "@vueform/multiselect";
import moment from "moment-timezone";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";
type FormValues = {
  name?: string;
  occupations?: Occupation[];
  positions?: Position[];
  startDate?: Date;
  endDate?: Date;
};

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    SearchField,
    FileSelectorButton,
    EmployeeForm,
    Multiselect,
    Datepicker
  }
})
export default class Employees extends Vue {
  private readonly storeInstance = Store.getInstance();
  instruction = this.storeInstance.getInstruction();
  employees: GetEmployeeProfile[] = [];
  employee: Employee | {} = {};
  header = [
    { title: "", key: "image", width: 50, type: "avatar" },
    { title: "名前", sortable: true, key: "name", width: 150 },
    { title: "部署", sortable: true, key: "departmentName", width: 150 },
    { title: "職種", key: "occupationName", width: 150 },
    { title: "役職", key: "positionName", width: 150 },
    { title: "最終受検日", key: "last_exam_date", width: 90 },
    {
      title: "受検結果",
      type: "button",
      key: "result",
      width: 120,
      disabled: (row: GetEmployeeProfile) => {
        return !this.appropriateResults || !row.lastExamDate;
      }
    },
    { title: "詳細", type: "button", key: "edit", width: 100, disabled: () => !this.isReadableEmployee }
  ];
  filterColumns = ["name", "departmentName", "occupationName", "positionName", "last_exam_date"];
  @Ref("modal") modal!: HTMLElement;
  formKey = 0;
  availableCreateEmployee = RoleName.ClientAdmin;
  availableRegistration = false;
  appropriateResults = false;
  isReadableEmployee = false;
  isEditableEmployee = false;
  occupations = {};
  positions = {};
  occValues: [] = [];
  posValues: [] = [];
  offset?: string;
  totalCount = "-";
  FormValues: FormValues = { occupations: [], positions: [] };
  locale = ja;
  selectedDepartmentPath: string = "";

  async beforeMount() {
    if (!this.storeInstance.getRole().id) await this.storeInstance.getRoleApi();
    this.availableRegistration = this.$role.isStrongerThan(RoleName.ClientAdmin);
    this.appropriateResults = this.$role.isReadable("employeeStats");
    this.isReadableEmployee = this.$role.isReadable("employee");
    this.isEditableEmployee = this.$role.isEditable("employee");
    const commonCondtions = {
      mode: "tags",
      label: "name",
      trackBy: "name",
      valueProp: "id",
      max: 5,
      closeOnSelect: false,
      searchable: true,
      noResultsText: "候補がありません"
    };
    this.occupations = Object.assign({}, commonCondtions, { options: this.storeInstance.getOccupations() });
    this.positions = Object.assign({}, commonCondtions, { options: this.storeInstance.getPositions() });
    this.setDepartmentPath();
  }
  async getTableData() {
    const departmentId = this.$route.params["departmentId"] as string;
    if (this.offset === null) {
      return [];
    }
    const response = await new DepartmentApi().getEmployees(
      Object.assign({
        limit: "100",
        name: this.FormValues.name ? this.FormValues.name : undefined,
        offset: this.offset ? this.offset : undefined,
        departmentId,
        occupations: this.FormValues.occupations?.length ? this.FormValues.occupations : undefined,
        positions: this.FormValues.positions?.length ? this.FormValues.positions : undefined,
        examStartDate: this.FormValues.startDate ? moment(this.FormValues.startDate).format("YYYY-MM-DD") : undefined,
        examEndDate: this.FormValues.endDate ? moment(this.FormValues.endDate).format("YYYY-MM-DD") : undefined
      })
    );
    this.offset = response.data.offset;
    this.totalCount = response.data.totalCount || "-";
    if (!response.data.lists || !response.data.lists.length) return [];
    return response.data.lists.map(datum =>
      Object.assign(
        {
          name: `${datum.lastName} ${datum.firstName}`,
          departmentName: datum.department?.name || "-",
          occupationName: datum.occupation?.name || "-",
          positionName: datum.position?.name || "-",
          last_exam_date: datum.lastExamDate ? moment(datum.lastExamDate).format("YYYY/MM/DD") : "-"
        },
        datum
      )
    );
  }
  @Watch("$route")
  async onRouteChanged(to: RouteLocation) {
    const path = to.path.split("/").filter(Boolean);
    if (path.slice(-1)[0] === "employees") {
      this.onEmployeeSearch();
      this.setDepartmentPath();
    }
  }
  onAddButtonClick() {
    this.employee = {
      username: "",
      lastName: "",
      firstName: "",
      email: "",
      career: "new",
      departmentId: "",
      occupationId: "",
      positionId: "",
      role: "4",
      contractType: "employee",
      enrollment: "enrolled",
      retirementReasons: [],
      retirementDate: ""
    };
    this.formKey = Date.now();
    this.prepareForm();
  }
  private closeForm() {
    UIkit.modal(this.modal).hide();
  }
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
  private prepareForm() {
    UIkit.modal(this.modal).show();
  }
  async onColumnClick(location: { row: number; col: string; el: HTMLElement; rowData: GetEmployeeProfile }) {
    if (location.col === "result") {
      this.$router.push({
        name: "EmployeeStats",
        params: {
          clientId: this.$route.params["clientId"],
          departmentId: this.$route.params["departmentId"],
          peopleId: location.rowData.id
        }
      });
    } else if (location.col === "edit") {
      const response = await new EmployeeApi().getEmployee({
        departmentId: "all",
        employeeId: location.rowData.id
      });
      this.employee = Object.assign({}, response.data, {
        departmentId: response.data.department?.id || "",
        contractType: response.data.contractType?.code || "",
        career: response.data.career?.code || "",
        aditionalDepartmentIds: response.data.additionalDepartments?.length
          ? response.data.additionalDepartments?.map(r => [r.id]).reduce((a, b) => a.concat(b))
          : null,
        anotherDepartmentIds: response.data.anotherDepartments?.length
          ? response.data.anotherDepartments?.map(r => [r.id]).reduce((a, b) => a.concat(b))
          : null,
        occupationId: response.data.occupation?.id || "",
        positionId: response.data.position?.id || ""
      });
      this.prepareForm();
    }
  }
  onEmployeeSearch() {
    const table = this.$refs.tableComponent as Table;
    if (table) {
      this.offset = undefined;
      table.reset();
    }
  }
  async onSubmitted() {
    this.closeForm();
    await new AuthApi().authenticate({}).then(resp => {
      if (resp.status === 200) {
        Store.getInstance().setAuth(resp.data);
      }
    });
    this.onEmployeeSearch();
  }
  async onSelectExcel(file: File) {
    Store.getInstance().setIsLoading(true);
    try {
      await new SystemApi().uploadExcel({
        file,
        item: UploadItemEnum.Employees
      });
      Store.getInstance().setInstructionContent(InstructionLevel.Info, "登録を受け付けました");
      this.onEmployeeSearch();
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  async onResultsExportButtonClick() {
    Store.getInstance().setIsLoading(true);
    const encoding = require("encoding-japanese");
    const departmentId = this.$route.params["departmentId"] as string;
    const response = await new SystemApi().asexport({ type: AsExportTypeEnum.EmployeeWithDept, departmentId });
    const str_array = encoding.stringToCode(response.data);
    const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
    const uint8_array = new Uint8Array(sjis_array);

    const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));
    const anchor = document.createElement("a");
    Store.getInstance().setIsLoading(false);
    anchor.href = url;
    anchor.setAttribute("download", `受検結果ダウンロード（従業員）_${new Date().toISOString()}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  async onDownloadTemplateFile(event: Event) {
    event.preventDefault();
    Store.getInstance().setIsLoading(true);
    try {
      const response = await new ClientApi().getXlsTemplate({ peopleType: "employee" }, { responseType: "arraybuffer" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Employees-import-Template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  resetConditions() {
    this.FormValues = { occupations: [], positions: [] };
    this.onEmployeeSearch();
  }
  setDepartmentPath() {
    const departmentId = this.$route.params["departmentId"] as string;
    this.selectedDepartmentPath = this.storeInstance.getDepartmentPath(departmentId);
  }
}
</script>
